import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { formatFileSize, formatDate } from './utils';
import config from './config';

const AssetsPage = () => {
  const { auth, logout, username } = useAuth();
  const [versions, setVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [installers, setInstallers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  useEffect(() => {
    fetchVersions();
  }, []);

  useEffect(() => {
    if (selectedVersion) {
      fetchInstallers(selectedVersion);
    }
  }, [selectedVersion]);

  const fetchVersions = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${config.apiDomain}/api/v1/versions`, {
        headers: {
          'Authorization': `Basic ${auth}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch versions');
      }

      const data = await response.json();
      setVersions(data);
      setSelectedVersion(data[0]); // Select the first version by default
      setError(null);
    } catch (err) {
      setError(err.message);
      setVersions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchInstallers = async (version) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${config.apiDomain}/api/v1/installers/${version}`, {
        headers: {
          'Authorization': `Basic ${auth}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch installers');
      }

      const data = await response.json();
      setInstallers(data);
      setError(null);
    } catch (err) {
      setError(err.message);
      setInstallers([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async (installerPath) => {
    try {
      const response = await fetch(`${config.apiDomain}/api/v1/download?fpath=${encodeURIComponent(installerPath)}`, {
        headers: {
          'Authorization': `Basic ${auth}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to get download link');
      }

      const downloadUrl = await response.text();
      window.location.href = downloadUrl; // Trigger the file download
    } catch (err) {
      setError(err.message);
    }
  };

  const handleChangelogDownload = async (installerFileName) => {
    try {
      // Replace the file extension with .txt
      const changelogFileName = installerFileName.replace(/\.[^/.]+$/, ".log.txt");
      
      const response = await fetch(`${config.apiDomain}/api/v1/download?fpath=${encodeURIComponent("installer-changelogs/"+changelogFileName)}`, {
        headers: {
          'Authorization': `Basic ${auth}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to get changelog download link');
      }

      const downloadUrl = await response.text();
      window.open(downloadUrl, '_blank');
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteVersion = async (installerFileName) => {
    // Add confirmation step
    const isConfirmed = window.confirm(`Are you sure you want to delete ${installerFileName}? This action cannot be undone.`);
    if (!isConfirmed) {
      return; // Exit the function if the user cancels the deletion
    }

    // Add confirmation step
    const isConfirmed2 = window.confirm(`Are you sure sure? ${installerFileName} is almost deleted forever.`);
    if (!isConfirmed2) {
      return; // Exit the function if the user cancels the deletion
    }

    try {
      setIsLoading(true);
      setError(null);
      setSuccessMessage(null);

      const response = await fetch(`${config.apiDomain}/api/v1/installers/delete/${encodeURIComponent(installerFileName)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Basic ${auth}`,
          'Content-Type': 'application/json',
        },
        body: null,
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete version');
      }
      setSuccessMessage(`Successfully deleted ${installerFileName}`);
      // Refresh the installers list for the current version
      await fetchInstallers(selectedVersion);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePromoteVersion = async (installerFileName) => {

    const isConfirmed = window.confirm(`Be patient! it might take a few minutes....`);
    if (!isConfirmed) {
      return; // Exit the function if the user cancels the deletion
    }

    try {
      setIsLoading(true);
      setError(null);
      setSuccessMessage(null);

      const response = await fetch(`${config.apiDomain}/api/v1/installers/promote/${encodeURIComponent(installerFileName)}`, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${auth}`,
          'Content-Type': 'application/json',
        },
        body: null,
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to promote version');
      }
      setSuccessMessage(`Successfully promoted ${installerFileName}`);
      // Refresh the installers list for the current version
      await fetchInstallers(selectedVersion);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="assets-page">
      <h2>Available Installers</h2>
      {isLoading && <p>Loading...</p>}
      {error && (<><p className="error">Error: {error}</p><button className="reload" onClick={fetchVersions}>Reload</button>&nbsp;</>)}
      {!isLoading && !error && (
        <>
          <div className="version-tabs">
            {versions.map((version) => (
              <button
                key={version}
                className={`tab ${selectedVersion === version ? 'active' : ''}`}
                onClick={() => setSelectedVersion(version)}
              >
                {version}
              </button>
            ))}
          </div>
          <h3>Installers for version {selectedVersion}</h3>
          <ul className="installer-list">
            {installers.map((installer) => (
              <li key={installer.name} className="installer-item">
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleDownload(installer.path);
                  }}
                  className="installer-link"
                >
                  <div className="installer-main-info">
                    <span className="installer-name">{installer.name}</span>
                    <div className="installer-tags">
                      {installer.tags && installer.tags.map((tag, index) => (
                        <span key={index}
                              className={`installer-tag ${tag === "PROD" ? "prod-tag" : ""}`}>{tag}</span>
                      ))}
                    </div>
                  </div>
                  <div className="installer-details">
                    <div className="installer-details-row">
                      <button
                        className="button-link changelog-link"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleChangelogDownload(installer.name);
                        }}
                      >
                        Changelog
                      </button>
                      <span className="installer-size">{formatFileSize(installer.size)}</span>
                    </div>
                    <span className="installer-date">{formatDate(installer.date)}</span>
                    {username === 'admin' && (
                      <>
                        <button
                            className="button-link delete-link"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteVersion(installer.name);
                            }}
                        >
                          Delete
                        </button>
                        <button
                            className="button-link promote-link"
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePromoteVersion(installer.name);
                            }}
                        >
                          GoToProd
                        </button>
                      </>
                    )}
                  </div>
                </a>
              </li>
            ))}
          </ul>
        </>
      )}
      <button onClick={logout}>Logout</button>
    </div>
  );
};

export default AssetsPage;